/**
 * StatDePlataTab.jsx - Componentă pentru generarea Statului de Plată
 * Format bazat pe modelul românesc standard de lichidare/stat de plată
 */

import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Calculator, Info, Coins, Lock, Unlock, Settings2, PenLine, Save, Check, Loader2, CalendarClock } from "lucide-react";
import axios from "axios";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import * as XLSX from "xlsx";
import { checkAndDeductCredits } from "@/utils/creditSystem";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Contribuții sociale standard România
const CAS_RATE = 0.25; // 25% - Contribuții Asigurări Sociale (pensie)
const CASS_RATE = 0.10; // 10% - Contribuții Asigurări Sociale de Sănătate
const IMPOZIT_RATE = 0.10; // 10% - Impozit pe venit

// Ore standard pe lună
const STANDARD_HOURS_PER_MONTH = 160; // 8h x 20 zile

export default function StatDePlataTab({ 
  workers, 
  month, 
  year, 
  allData = {}, 
  companyId,
  companyName = "Firma",
  onCreditsChange 
}) {
  const [selectedWorkers, setSelectedWorkers] = useState({});
  const [salaryData, setSalaryData] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [paymentUnlocked, setPaymentUnlocked] = useState(false);
  const [signatures, setSignatures] = useState({});
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [tichetValoareZi, setTichetValoareZi] = useState(40); // Valoare tichet de masă pe zi (RON)
  const [initialized, setInitialized] = useState(false); // Flag pentru a preveni suprascrierea datelor
  const [savingPayroll, setSavingPayroll] = useState(false); // Stare salvare setări salarii
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp ultima salvare
  const [dirty, setDirty] = useState(false); // Există modificări nesalvate
  
  // Verifică dacă plata pentru luna curentă a fost deja făcută
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/credits/check`, {
          params: { 
            company_id: companyId, 
            action_code: 'stat_de_plata_export',
            reference_id: `stat_plata_${year}_${month}`
          }
        });
        setPaymentUnlocked(response.data.already_paid || false);
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    };
    
    if (companyId) {
      checkPaymentStatus();
    }
  }, [companyId, month, year]);
  
  // Încarcă semnăturile pentru luna/anul selectat
  useEffect(() => {
    const fetchSignatures = async () => {
      if (!companyId) return;
      
      setLoadingSignatures(true);
      try {
        const response = await axios.get(`${API}/signatures/company/${companyId}/${month}/${year}`);
        // Convertește array în map pentru acces rapid
        const signaturesMap = {};
        if (Array.isArray(response.data)) {
          response.data.forEach(sig => {
            signaturesMap[sig.user_id] = sig;
          });
        }
        setSignatures(signaturesMap);
      } catch (err) {
        console.error('Error fetching signatures:', err);
        setSignatures({});
      } finally {
        setLoadingSignatures(false);
      }
    };
    
    fetchSignatures();
  }, [companyId, month, year]);
  
  // Inițializare selecție și date salariu - încarcă mai întâi setările salvate din backend
  useEffect(() => {
    if (!workers || workers.length === 0) return;
    if (initialized) return;
    if (!companyId) return;

    let cancelled = false;
    (async () => {
      let saved = null;
      try {
        const r = await axios.get(`${API}/payroll/settings`, {
          params: { company_id: companyId, year, month },
        });
        saved = r.data || null;
      } catch (err) {
        console.error("Eroare la încărcarea setărilor de salarii:", err);
      }
      if (cancelled) return;

      const savedWorkers = (saved && saved.workers) || {};
      const initialSelection = {};
      const initialSalary = {};
      workers.forEach((w) => {
        const sv = savedWorkers[w.id];
        // Selectie: foloseste valoarea salvata; daca lipseste -> admin deselectat implicit
        initialSelection[w.id] = sv?.selected != null ? !!sv.selected : !w.is_administrator;
        initialSalary[w.id] = {
          salarBrut: sv?.salar_brut ?? (w.salary || 4050),
          bonusuri: sv?.bonusuri ?? 0,
          deducerePersonala: sv?.deducere_personala ?? 0,
        };
      });
      setSelectedWorkers(initialSelection);
      setSalaryData(initialSalary);
      if (saved && saved.tichet_valoare_zi) setTichetValoareZi(saved.tichet_valoare_zi);
      if (saved && !saved.inherited_from && saved.workers) setLastSavedAt(saved.updated_at || null);
      setInitialized(true);
      setDirty(false);
    })();

    return () => { cancelled = true; };
  }, [workers, initialized, companyId, year, month]);

  // Adaugă workeri noi după inițializare (fără a suprascrie datele existente)
  useEffect(() => {
    if (!initialized) return;
    if (!workers || workers.length === 0) return;
    setSelectedWorkers(prev => {
      const updated = { ...prev };
      let changed = false;
      workers.forEach(w => {
        if (!(w.id in updated)) { updated[w.id] = !w.is_administrator; changed = true; }
      });
      return changed ? updated : prev;
    });
    setSalaryData(prev => {
      const updated = { ...prev };
      let changed = false;
      workers.forEach(w => {
        if (!(w.id in updated)) {
          updated[w.id] = { salarBrut: w.salary || 4050, bonusuri: 0, deducerePersonala: 0 };
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [workers, initialized]);
  
  // Calculează datele de pontaj pentru fiecare angajat
  const calculateWorkerStats = (workerId) => {
    const data = allData[workerId] || {};
    
    // Zile și ore lucrate
    const attendances = data.attendances || [];
    const zileLucrate = attendances.length;
    const oreLucrate = attendances.reduce((sum, a) => sum + (a.hours_worked || 8), 0);
    
    // Ore suplimentare
    const overtimes = data.overtime || [];
    const oreSuplimentare = overtimes.reduce((sum, o) => sum + (o.hours || 0), 0);
    const oreSuplimentareEchivalent = overtimes.reduce((sum, o) => sum + (o.equivalent_hours || o.hours * 1.5), 0);
    
    // Concedii
    const leaves = data.leaves || [];
    const zileConcediu = leaves
      .filter(l => l.status === 'approved')
      .reduce((sum, l) => sum + (l.days_count || 1), 0);
    
    const zileCO = leaves
      .filter(l => l.type === 'CO' && l.status === 'approved')
      .reduce((sum, l) => sum + (l.days_count || 1), 0);
    
    const zileCM = leaves
      .filter(l => l.type === 'CM' && l.status === 'approved')
      .reduce((sum, l) => sum + (l.days_count || 1), 0);
    
    // Absențe
    const absences = data.absences || [];
    const zileAbsente = absences.filter(a => a.type === 'absence').length;
    
    return {
      zileLucrate,
      oreLucrate,
      oreSuplimentare,
      oreSuplimentareEchivalent,
      zileConcediu,
      zileCO,
      zileCM,
      zileAbsente
    };
  };
  
  // Calculează stat de plată pentru un angajat
  // Formula conform calculator-salarii.ro (2026)
  const calculatePayroll = (workerId) => {
    const stats = calculateWorkerStats(workerId);
    const raw = salaryData[workerId] || {};
    // Coerce string/empty -> number for math
    const salary = {
      salarBrut: Number(raw.salarBrut) || 0,
      bonusuri: Number(raw.bonusuri) || 0,
      deducerePersonala: Number(raw.deducerePersonala) || 0,
    };

    // Calculează tikete automat: zile lucrate * valoare tichet pe zi
    const ticheteCalculate = stats.zileLucrate * (Number(tichetValoareZi) || 0);
    
    // Salariu de bază proporțional cu orele lucrate
    const oreNormale = STANDARD_HOURS_PER_MONTH;
    const proportie = Math.min(stats.oreLucrate / oreNormale, 1);
    
    // Venit brut = salar de bază proporțional + bonusuri
    const salarProportional = salary.salarBrut * proportie;
    const venitBrut = salarProportional + salary.bonusuri;
    
    // CAS 25% - se calculează pe salariul brut
    const cas = venitBrut * CAS_RATE;
    
    // CASS 10% - se calculează pe (salariu brut + valoare tichete)
    // Conform calculator-salarii.ro: CASS include și tichetele
    const bazaCASS = venitBrut + ticheteCalculate;
    const cass = bazaCASS * CASS_RATE;
    
    // Bază impozabilă = Brut - CAS - CASS
    const venitBazaDeduct = venitBrut - cas - cass;
    
    // Impozit pe venit 10% - se calculează pe (bază impozabilă + tichete - deducere personală)
    // Tichetele sunt impozitate
    const bazaImpozit = venitBazaDeduct + ticheteCalculate - salary.deducerePersonala;
    const venitBazaCalc = Math.max(0, bazaImpozit);
    
    // Impozit pe venit
    const impozit = venitBazaCalc * IMPOZIT_RATE;
    
    // Salariu net = Brut - CAS - CASS - Impozit
    const salarNet = venitBrut - cas - cass - impozit;
    
    // Total de plată (salariu net + tichete)
    const totalDePlata = salarNet + ticheteCalculate;
    
    return {
      ...stats,
      salarBrut: salary.salarBrut,
      venitBrut: Math.round(venitBrut),
      cas: Math.round(cas),
      cass: Math.round(cass),
      venitBazaDeduct: Math.round(venitBazaDeduct),
      deducerePersonala: salary.deducerePersonala,
      venitBazaCalc: Math.round(venitBazaCalc),
      impozit: Math.round(impozit),
      salarNet: Math.round(salarNet),
      tichete: Math.round(ticheteCalculate),
      bonusuri: salary.bonusuri,
      totalDePlata: Math.round(totalDePlata)
    };
  };
  
  // Lista angajaților selectați cu calcule
  const payrollData = useMemo(() => {
    return workers
      .filter(w => selectedWorkers[w.id])
      .map(w => ({
        ...w,
        payroll: calculatePayroll(w.id)
      }));
  }, [workers, selectedWorkers, allData, salaryData, tichetValoareZi]);
  
  // Totaluri
  const totals = useMemo(() => {
    return payrollData.reduce((acc, w) => ({
      oreLucrate: acc.oreLucrate + w.payroll.oreLucrate,
      oreSuplimentare: acc.oreSuplimentare + w.payroll.oreSuplimentare,
      zileLucrate: acc.zileLucrate + w.payroll.zileLucrate,
      zileConcediu: acc.zileConcediu + w.payroll.zileConcediu,
      venitBrut: acc.venitBrut + w.payroll.venitBrut,
      cas: acc.cas + w.payroll.cas,
      cass: acc.cass + w.payroll.cass,
      impozit: acc.impozit + w.payroll.impozit,
      salarNet: acc.salarNet + w.payroll.salarNet,
      tichete: acc.tichete + w.payroll.tichete,
      totalDePlata: acc.totalDePlata + w.payroll.totalDePlata
    }), {
      oreLucrate: 0, oreSuplimentare: 0, zileLucrate: 0, zileConcediu: 0,
      venitBrut: 0, cas: 0, cass: 0, impozit: 0, salarNet: 0, tichete: 0, totalDePlata: 0
    });
  }, [payrollData]);
  
  // Toggle selecție angajat
  const toggleWorker = (workerId) => {
    setSelectedWorkers(prev => ({
      ...prev,
      [workerId]: !prev[workerId]
    }));
    setDirty(true);
  };
  
  // Resetează inițializarea când se schimbă luna / anul ca să încarce setările lunii noi
  useEffect(() => {
    setInitialized(false);
    setDirty(false);
    setLastSavedAt(null);
  }, [month, year, companyId]);

  // Update salariu pentru un angajat (acceptă "" în timpul editării; calculul folosește Number())
  const updateSalary = (workerId, field, value) => {
    setSalaryData(prev => ({
      ...prev,
      [workerId]: {
        ...prev[workerId],
        [field]: value === "" ? "" : (parseFloat(value) || 0)
      }
    }));
    setDirty(true);
  };

  // Salvează setările de salarii pe backend
  const handleSavePayroll = async () => {
    if (!companyId) return;
    setSavingPayroll(true);
    try {
      const workersPayload = {};
      Object.keys(salaryData).forEach((wid) => {
        const s = salaryData[wid];
        workersPayload[wid] = {
          salar_brut: Number(s.salarBrut) || 0,
          bonusuri: Number(s.bonusuri) || 0,
          deducere_personala: Number(s.deducerePersonala) || 0,
          selected: !!selectedWorkers[wid],
        };
      });
      const r = await axios.put(`${API}/payroll/settings`, {
        company_id: companyId,
        year, month,
        tichet_valoare_zi: Number(tichetValoareZi) || 40,
        workers: workersPayload,
      });
      setLastSavedAt(r.data?.saved_at || new Date().toISOString());
      setDirty(false);
      toast.success("Salariile au fost salvate pentru această lună");
    } catch (err) {
      console.error("Eroare la salvare salarii:", err);
      toast.error("Nu am putut salva setările. Reîncearcă.");
    } finally {
      setSavingPayroll(false);
    }
  };

  // Copiază salariile din ultima lună salvată anterior lunii curente
  const handleCopyPrior = async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/payroll/settings/latest-prior`, {
        params: { company_id: companyId, year, month },
      });
      if (!r.data?.found) {
        toast.info("Nu există salarii salvate într-o lună anterioară");
        return;
      }
      const priorWorkers = r.data.workers || {};
      const nextSalary = {};
      const nextSelection = {};
      workers.forEach((w) => {
        const sv = priorWorkers[w.id];
        nextSalary[w.id] = {
          salarBrut: sv?.salar_brut ?? (salaryData[w.id]?.salarBrut ?? (w.salary || 4050)),
          bonusuri: sv?.bonusuri ?? (salaryData[w.id]?.bonusuri ?? 0),
          deducerePersonala: sv?.deducere_personala ?? (salaryData[w.id]?.deducerePersonala ?? 0),
        };
        nextSelection[w.id] = sv?.selected != null ? !!sv.selected : (selectedWorkers[w.id] ?? !w.is_administrator);
      });
      setSalaryData(nextSalary);
      setSelectedWorkers(nextSelection);
      if (r.data.tichet_valoare_zi) setTichetValoareZi(r.data.tichet_valoare_zi);
      setDirty(true);
      const m = r.data.from?.month, y = r.data.from?.year;
      toast.success(`Salariile au fost copiate din ${String(m).padStart(2, "0")}/${y}. Apasă „Salvează" ca să le păstrezi.`);
    } catch (err) {
      console.error("Eroare la copiere salarii:", err);
      toast.error("Nu am putut copia salariile.");
    }
  };
  
  // Verifică și procesează plata pentru export
  const handleExport = async (exportType) => {
    if (!paymentUnlocked) {
      // Trebuie să plătească 5 credite
      const result = await checkAndDeductCredits(
        companyId,
        'stat_de_plata_export',
        `stat_plata_${year}_${month}`,
        (msg) => toast.warning(msg),
        (msg) => toast.error(msg)
      );
      
      if (!result.canProceed) {
        return;
      }
      
      setPaymentUnlocked(true);
      if (onCreditsChange) {
        onCreditsChange();
      }
      toast.success("Plată procesată! Puteți descărca nelimitat în această lună.");
    }
    
    if (exportType === 'pdf') {
      exportToPDF();
    } else {
      exportToExcel();
    }
  };
  
  // Export PDF
  const exportToPDF = async () => {
    setExporting(true);
    
    try {
      const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long' });
      
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();
      
      // Header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(companyName.toUpperCase(), 14, 15);
      
      doc.setFontSize(14);
      doc.text(`STAT DE PLATĂ SALARII - ${monthName.toUpperCase()} ${year}`, pdfWidth / 2, 25, { align: 'center' });
      
      // Tabel cu date - adăugăm zile prezente și status semnătură
      const tableData = payrollData.map((w, idx) => {
        const hasSig = signatures[w.id]?.signature_image;
        return [
          idx + 1,
          w.name,
          w.payroll.zileLucrate,
          w.payroll.oreLucrate,
          w.payroll.oreSuplimentare,
          w.payroll.salarBrut,
          w.payroll.venitBrut,
          w.payroll.cas,
          w.payroll.cass,
          w.payroll.venitBazaCalc,
          w.payroll.impozit,
          w.payroll.salarNet,
          w.payroll.tichete,
          w.payroll.totalDePlata,
          hasSig ? '✓' : '-'
        ];
      });
      
      // Rând totaluri
      const signedCount = Object.keys(signatures).filter(uid => 
        payrollData.some(w => w.id === uid)
      ).length;
      
      tableData.push([
        '', 'TOTAL',
        totals.zileLucrate,
        totals.oreLucrate,
        totals.oreSuplimentare,
        '-',
        totals.venitBrut,
        totals.cas,
        totals.cass,
        '-',
        totals.impozit,
        totals.salarNet,
        totals.tichete,
        totals.totalDePlata,
        `${signedCount}/${payrollData.length}`
      ]);
      
      autoTable(doc, {
        startY: 35,
        head: [[
          'Nr.', 'Nume Prenume', 'Zile\nprez.', 'Ore\nlucr.', 'Ore\nsupl.', 'Salar\nbază', 'Venit\nbrut',
          'CAS\n25%', 'CASS\n10%', 'Bază\nimp.',
          'Imp.\n10%', 'Salar\nnet', 'Tich.', 'Total\nplată', 'Semn.'
        ]],
        body: tableData,
        theme: 'grid',
        styles: { 
          fontSize: 7,
          cellPadding: 1,
          halign: 'center',
          valign: 'middle'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 6,
          fontStyle: 'bold',
          halign: 'center'
        },
        columnStyles: {
          0: { cellWidth: 8 },
          1: { cellWidth: 35, halign: 'left' },
          2: { cellWidth: 12 },
          3: { cellWidth: 12 },
          4: { cellWidth: 12 },
          5: { cellWidth: 15 },
          6: { cellWidth: 15 },
          7: { cellWidth: 15 },
          8: { cellWidth: 12 },
          9: { cellWidth: 15 },
          10: { cellWidth: 12 },
          11: { cellWidth: 15 },
          12: { cellWidth: 15 },
          13: { cellWidth: 15 },
          14: { cellWidth: 12 }
        },
        didParseCell: function(data) {
          // Stil pentru rândul de total
          if (data.row.index === tableData.length - 1) {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.fillColor = [236, 240, 241];
          }
          // Semnătură prezentă - verde
          if (data.column.index === 14 && data.cell.raw === '✓') {
            data.cell.styles.fillColor = [200, 250, 200];
            data.cell.styles.textColor = [0, 100, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      });
      
      // Footer pagina 1
      const finalY = doc.lastAutoTable.finalY + 15;
      doc.setFontSize(10);
      doc.text('Administrator,', 30, finalY);
      doc.text('Întocmit,', pdfWidth - 50, finalY);
      
      doc.setFontSize(7);
      doc.text(`Generat: ${new Date().toLocaleString('ro-RO')}`, 14, pdfHeight - 10);
      doc.text('e-Tesa', pdfWidth - 25, pdfHeight - 10);
      
      // === PAGINA 2: Semnăturile angajaților ===
      const workersWithSignatures = payrollData.filter(w => signatures[w.id]?.signature_image);
      
      if (workersWithSignatures.length > 0) {
        doc.addPage();
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`SEMNĂTURI ANGAJAȚI - ${monthName.toUpperCase()} ${year}`, pdfWidth / 2, 15, { align: 'center' });
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.text('Semnăturile digitale sunt înregistrate conform Regulamentului eIDAS și Legii 455/2001.', pdfWidth / 2, 22, { align: 'center' });
        
        let sigY = 30;
        const sigWidth = 60;
        const sigHeight = 25;
        const sigsPerRow = 4;
        const sigMargin = 10;
        const sigSpacing = (pdfWidth - 2 * sigMargin - sigsPerRow * sigWidth) / (sigsPerRow - 1);
        
        for (let i = 0; i < workersWithSignatures.length; i++) {
          const worker = workersWithSignatures[i];
          const sig = signatures[worker.id];
          
          const col = i % sigsPerRow;
          const rowNum = Math.floor(i / sigsPerRow);
          const x = sigMargin + col * (sigWidth + sigSpacing);
          const y = sigY + rowNum * (sigHeight + 22);
          
          // Pagină nouă dacă e necesar
          if (y + sigHeight + 22 > pdfHeight - 20) {
            doc.addPage();
            sigY = 20;
          }
          
          // Chenar semnătură
          doc.setDrawColor(80, 80, 80);
          doc.setLineWidth(0.3);
          doc.rect(x, y, sigWidth, sigHeight);
          
          // Fundal alb
          doc.setFillColor(255, 255, 255);
          doc.rect(x + 0.2, y + 0.2, sigWidth - 0.4, sigHeight - 0.4, 'F');
          
          // Adaugă imaginea semnăturii
          if (sig.signature_image && sig.signature_image.length > 100) {
            try {
              let imageData = sig.signature_image;
              if (!imageData.startsWith('data:')) {
                imageData = 'data:image/png;base64,' + imageData;
              }
              doc.addImage(imageData, 'PNG', x + 1, y + 1, sigWidth - 2, sigHeight - 2);
            } catch (imgError) {
              console.error('Error adding signature image:', imgError);
              doc.setFontSize(8);
              doc.setTextColor(150, 150, 150);
              doc.text('[Eroare imagine]', x + sigWidth / 2, y + sigHeight / 2, { align: 'center' });
              doc.setTextColor(0, 0, 0);
            }
          }
          
          // Nume angajat
          doc.setFontSize(8);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text(worker.name.substring(0, 25), x + sigWidth / 2, y + sigHeight + 5, { align: 'center' });
          
          // Data semnării și detalii
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(6);
          doc.setTextColor(80, 80, 80);
          
          const signedDate = sig.signed_at ? new Date(sig.signed_at).toLocaleString('ro-RO', { 
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
          }) : 'N/A';
          
          doc.text(`Semnat: ${signedDate}`, x + sigWidth / 2, y + sigHeight + 9, { align: 'center' });
          
          // Semnătură count și IP
          doc.setFontSize(5);
          doc.text(`(${sig.signature_count || 1}/2) | IP: ${sig.ip_address || 'N/A'}`, x + sigWidth / 2, y + sigHeight + 13, { align: 'center' });
          doc.setTextColor(0, 0, 0);
        }
        
        // Notă juridică la finalul paginii de semnături
        doc.setFontSize(6);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        doc.text('Pentru fiecare semnătură s-au înregistrat: data, ora, dispozitivul și adresa IP.', pdfWidth / 2, pdfHeight - 10, { align: 'center' });
        doc.setTextColor(0, 0, 0);
      }
      
      doc.save(`stat_plata_${monthName}_${year}.pdf`);
      toast.success("Stat de plată PDF generat cu semnături!");
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Eroare la generarea PDF-ului");
    } finally {
      setExporting(false);
    }
  };
  
  // Export Excel
  const exportToExcel = () => {
    setExporting(true);
    
    try {
      const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long' });
      const wb = XLSX.utils.book_new();
      
      // Sheet 1: Stat de plată
      const data = [
        [companyName.toUpperCase()],
        [],
        [`STAT DE PLATĂ SALARII - ${monthName.toUpperCase()} ${year}`],
        [`Valoare tichet de masă/zi: ${tichetValoareZi} RON`],
        [],
        ['Nr.', 'Nume Prenume', 'Zile prez.', 'Ore lucr.', 'Ore supl.', 'Salar bază', 'Venit brut', 
         'CAS 25%', 'CASS 10%', 'Bază imp.',
         'Imp. 10%', 'Salar net', 'Tichete', 'Total plată']
      ];
      
      payrollData.forEach((w, idx) => {
        data.push([
          idx + 1,
          w.name,
          w.payroll.zileLucrate,
          w.payroll.oreLucrate,
          w.payroll.oreSuplimentare,
          w.payroll.salarBrut,
          w.payroll.venitBrut,
          w.payroll.cas,
          w.payroll.cass,
          w.payroll.venitBazaCalc,
          w.payroll.impozit,
          w.payroll.salarNet,
          w.payroll.tichete,
          w.payroll.totalDePlata
        ]);
      });
      
      // Rând total
      data.push([
        '', 'TOTAL',
        totals.zileLucrate,
        totals.oreLucrate,
        totals.oreSuplimentare,
        '',
        totals.venitBrut,
        totals.cas,
        totals.cass,
        '',
        totals.impozit,
        totals.salarNet,
        totals.tichete,
        totals.totalDePlata
      ]);
      
      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = [
        { wch: 5 }, { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 12 },
        { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 10 },
        { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 12 }
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'Stat de Plată');
      
      // Sheet 2: Detalii angajați
      const detailData = [
        ['DETALII ANGAJAȚI'],
        [],
        ['Nume', 'Zile lucrate', 'Ore lucrate', 'Ore supli.', 'Zile CO', 'Zile CM', 'Zile absente']
      ];
      
      payrollData.forEach(w => {
        detailData.push([
          w.name,
          w.payroll.zileLucrate,
          w.payroll.oreLucrate,
          w.payroll.oreSuplimentare,
          w.payroll.zileCO,
          w.payroll.zileCM,
          w.payroll.zileAbsente
        ]);
      });
      
      const ws2 = XLSX.utils.aoa_to_sheet(detailData);
      ws2['!cols'] = [
        { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }
      ];
      
      XLSX.utils.book_append_sheet(wb, ws2, 'Detalii');
      
      XLSX.writeFile(wb, `stat_plata_${monthName}_${year}.xlsx`);
      toast.success("Stat de plată Excel generat!");
      
    } catch (error) {
      console.error("Error generating Excel:", error);
      toast.error("Eroare la generarea Excel-ului");
    } finally {
      setExporting(false);
    }
  };
  
  const monthName = new Date(year, month - 1).toLocaleString('ro-RO', { month: 'long', year: 'numeric' });
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-blue-900 flex-wrap">
                <Calculator className="w-5 h-5" />
                Stat de Plată - {monthName.charAt(0).toUpperCase() + monthName.slice(1)}
                {/* Status credit — păstrăm lângă titlu ca să nu împingă butoanele pe mobil */}
                <Badge variant={paymentUnlocked ? "default" : "outline"}
                  className={`ml-1 text-[10px] ${paymentUnlocked ? "bg-green-600" : ""}`}
                  data-testid="stat-credit-badge"
                >
                  {paymentUnlocked ? (
                    <><Unlock className="w-3 h-3 mr-1" /> Deblocat</>
                  ) : (
                    <><Lock className="w-3 h-3 mr-1" /> 5 credite / lună</>
                  )}
                </Badge>
              </CardTitle>
              <CardDescription>
                Generează statul de plată lunar pentru angajații selectați
              </CardDescription>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Butoane export */}
              <Button
                variant="outline"
                onClick={() => setShowSettings(!showSettings)}
                className="border-amber-400 hover:bg-amber-50"
                data-testid="setari-salarii-btn"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                Setări salarii
              </Button>

              <Button
                onClick={() => handleExport('pdf')}
                disabled={exporting || payrollData.length === 0}
                className="bg-red-600 hover:bg-red-700"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </Button>

              <Button
                onClick={() => handleExport('excel')}
                disabled={exporting || payrollData.length === 0}
                className="bg-green-600 hover:bg-green-700"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>
      
      {/* Info */}
      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2">
        <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <p className="text-sm text-amber-800">
          <strong>Calcul conform calculator-salarii.ro (2026):</strong> CAS 25% din brut, 
          CASS 10% din (brut + tichete), Impozit 10% din (bază impozabilă + tichete - deducere). 
          Salariu minim 2026: 4050 lei. Costul exportului: 5 credite/lună.
        </p>
      </div>
      
      {/* Panel setări salarii */}
      {showSettings && (
        <Card className="border-amber-200 animate-in slide-in-from-top-2 duration-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-sm">Configurare salarii și deduceri</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyPrior}
                  data-testid="payroll-copy-prior-btn"
                  className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <CalendarClock className="w-3.5 h-3.5 mr-1" />
                  Copiază salariile din luna trecută
                </Button>
                {lastSavedAt && !dirty && (
                  <span className="text-[11px] text-emerald-600 inline-flex items-center gap-1" data-testid="payroll-saved-indicator">
                    <Check className="w-3 h-3" /> Salvat
                  </span>
                )}
                {dirty && (
                  <span className="text-[11px] text-amber-600 inline-flex items-center gap-1" data-testid="payroll-dirty-indicator">
                    Modificări nesalvate
                  </span>
                )}
                <Button
                  size="sm"
                  onClick={handleSavePayroll}
                  disabled={savingPayroll || !dirty}
                  data-testid="payroll-save-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"
                >
                  {savingPayroll ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Salvează salariile
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Setare globală pentru valoarea tichetului de masă */}
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Coins className="w-5 h-5 text-green-600" />
                  <Label className="text-sm font-medium text-green-800">Valoare tichet de masă / zi (RON):</Label>
                </div>
                <Input
                  type="number"
                  value={tichetValoareZi ?? ""}
                  onChange={(e) => { const v = e.target.value; setTichetValoareZi(v === "" ? "" : (parseFloat(v) || 0)); setDirty(true); }}
                  className="w-24 h-8 text-sm border-green-300"
                  data-testid="tichet-valoare-zi-input"
                  min="0"
                  step="1"
                />
                <span className="text-xs text-green-600">
                  (Se calculează automat: zile prezente × valoare/zi)
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {workers.map(worker => (
                <div 
                  key={worker.id}
                  className={`p-3 rounded-lg border ${selectedWorkers[worker.id] ? 'bg-white border-blue-300' : 'bg-slate-50 border-slate-200 opacity-60'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      checked={selectedWorkers[worker.id] || false}
                      onChange={() => toggleWorker(worker.id)}
                      className="rounded"
                      data-testid={`worker-checkbox-${worker.id}`}
                    />
                    <span className="font-medium text-sm">{worker.name}</span>
                    {worker.is_administrator && (
                      <Badge className="bg-amber-500 text-xs">Admin</Badge>
                    )}
                  </div>
                  
                  {selectedWorkers[worker.id] && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div>
                        <Label className="text-xs">Salar brut</Label>
                        <Input
                          type="number"
                          value={salaryData[worker.id]?.salarBrut ?? ""}
                          onChange={(e) => updateSalary(worker.id, 'salarBrut', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`salar-brut-${worker.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Bonusuri</Label>
                        <Input
                          type="number"
                          value={salaryData[worker.id]?.bonusuri ?? ""}
                          onChange={(e) => updateSalary(worker.id, 'bonusuri', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`bonusuri-${worker.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Deducere pers.</Label>
                        <Input
                          type="number"
                          value={salaryData[worker.id]?.deducerePersonala ?? ""}
                          onChange={(e) => updateSalary(worker.id, 'deducerePersonala', e.target.value)}
                          className="h-8 text-sm"
                          data-testid={`deducere-${worker.id}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Tabel preview */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="w-10">Nr.</TableHead>
                  <TableHead className="min-w-[180px]">Nume Prenume</TableHead>
                  <TableHead className="text-center">Zile prez.</TableHead>
                  <TableHead className="text-center">Ore lucr.</TableHead>
                  <TableHead className="text-center text-amber-700">Ore supl.</TableHead>
                  <TableHead className="text-right">Salar brut</TableHead>
                  <TableHead className="text-right">Venit brut</TableHead>
                  <TableHead className="text-right text-red-600">CAS 25%</TableHead>
                  <TableHead className="text-right text-red-600">CASS 10%</TableHead>
                  <TableHead className="text-right">Bază imp.</TableHead>
                  <TableHead className="text-right text-red-600">Impozit</TableHead>
                  <TableHead className="text-right font-bold">Salar net</TableHead>
                  <TableHead className="text-right text-green-700">Tichete</TableHead>
                  <TableHead className="text-right font-bold text-green-700">Total plată</TableHead>
                  <TableHead className="text-center w-24">Semnătură</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payrollData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="text-center py-8 text-slate-500">
                      Selectați angajați din "Setări salarii" pentru a genera statul de plată
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {payrollData.map((w, idx) => (
                      <TableRow key={w.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <TableCell className="font-medium">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{w.name}</TableCell>
                        <TableCell className="text-center font-medium text-blue-600">{w.payroll.zileLucrate}</TableCell>
                        <TableCell className="text-center">{w.payroll.oreLucrate}</TableCell>
                        <TableCell className="text-center text-amber-700 font-medium">
                          {w.payroll.oreSuplimentare > 0 ? w.payroll.oreSuplimentare : '-'}
                        </TableCell>
                        <TableCell className="text-right">{w.payroll.salarBrut.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{w.payroll.venitBrut.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-red-600">{w.payroll.cas.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-red-600">{w.payroll.cass.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{w.payroll.venitBazaCalc.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-red-600">{w.payroll.impozit.toLocaleString()}</TableCell>
                        <TableCell className="text-right font-bold">{w.payroll.salarNet.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-green-700" title={`${w.payroll.zileLucrate} zile × ${tichetValoareZi} RON`}>
                          {w.payroll.tichete.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-bold text-green-700">{w.payroll.totalDePlata.toLocaleString()}</TableCell>
                        <TableCell className="text-center">
                          {signatures[w.id]?.signature_image ? (
                            <div className="flex flex-col items-center gap-1">
                              <img 
                                src={signatures[w.id].signature_image.startsWith('data:') 
                                  ? signatures[w.id].signature_image 
                                  : `data:image/png;base64,${signatures[w.id].signature_image}`}
                                alt="Semnătură"
                                className="max-h-12 max-w-20 border border-green-300 rounded bg-white"
                                data-testid={`signature-img-${w.id}`}
                              />
                              <span className="text-[10px] text-green-600 font-medium">
                                {signatures[w.id].signature_count}/2
                              </span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs flex items-center justify-center gap-1">
                              <PenLine className="w-3 h-3" />
                              Nesemnat
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {/* Rând total */}
                    <TableRow className="bg-blue-50 font-bold border-t-2 border-blue-300">
                      <TableCell></TableCell>
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-center text-blue-600">{totals.zileLucrate}</TableCell>
                      <TableCell className="text-center">{totals.oreLucrate}</TableCell>
                      <TableCell className="text-center text-amber-700">{totals.oreSuplimentare || '-'}</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right">{totals.venitBrut.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-red-600">{totals.cas.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-red-600">{totals.cass.toLocaleString()}</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right text-red-600">{totals.impozit.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{totals.salarNet.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-green-700">{totals.tichete.toLocaleString()}</TableCell>
                      <TableCell className="text-right text-green-700">{totals.totalDePlata.toLocaleString()}</TableCell>
                      <TableCell className="text-center text-xs text-slate-500">
                        {Object.keys(signatures).length > 0 && (
                          <span className="text-green-600">{Object.keys(signatures).length} semnate</span>
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
